import React from "react";
import { Helmet } from "react-helmet";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { PageTitle } from "@/components/PageTitle";
import { CTASection } from "@/components/CTASection";
import { SectionTitle } from "@/components/SectionTitle";
import { FAQAccordion } from "@/components/FAQAccordion";
import { ServiceTestimonials } from "@/components/ServiceTestimonials";
import { Smile } from "lucide-react";

const CrownsBridges: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Crowns & Bridges in Nagpur | Cost of Crowns & Bridges in Nagpur</title>
        <meta
          name="description"
          content="Restore your smile with high-quality dental crowns and bridges at Asrani Dental Clinic in Nagpur. Expert restorative dentistry for damaged or missing teeth. Book your consultation today!"
        />
      </Helmet>

      <Header />

      <main className="pt-20">
        <PageTitle
          title="Crowns & Bridges"
        />

        {/* Service Overview Section */}
        <section className="py-16 bg-dental-offwhite">
          <div className="container-custom">
            <div className="flex flex-col md:flex-row gap-12 items-center">
              <div className="flex-1">
                <h2 className="text-3xl font-bold mb-6 text-dental-text">
                  Crowns & Bridges at Asrani Dental, Sadar
                </h2>
                <p className="mb-4">
                  Dental crowns and bridges are fixed prosthetic devices that restore damaged teeth or replace missing teeth, helping you regain both function and aesthetics. At Asrani Dental Clinic in Nagpur, we provide high-quality crowns and bridges crafted from premium materials for durable, natural-looking restorations.
                </p>
                <p className="mb-4">
                  A crown (or cap) completely covers a damaged tooth, restoring its shape, size, strength, and appearance. Bridges, on the other hand, are used to replace one or more missing teeth by spanning the gap and anchoring to the natural teeth or implants on either side.
                </p>
                <p className="mb-6">
                  Our experienced team uses the latest techniques and technologies to ensure your crown or bridge fits perfectly, functions properly, and blends seamlessly with your natural teeth.
                </p>
                <div className="flex items-center p-4 bg-white rounded-lg shadow-sm border border-gray-100">
                  <Smile className="w-8 h-8 text-dental-highlight mr-4" />
                  <div>
                    <h3 className="font-semibold">Precision-Crafted Restorations</h3>
                    <p className="text-sm text-gray-600">Our crowns and bridges are custom-made to match your natural teeth in color, shape, and function for a restoration that looks and feels completely natural.</p>
                  </div>
                </div>
              </div>
              <div className="flex-1">
                <img
                  src="/lovable-uploads/c2f099f1-73fc-4212-b5af-ed7a6dfdf04f.png"
                  alt="Dental Crowns and Bridges"
                  className="rounded-lg shadow-lg w-full"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Treatment Features */}
        <section className="py-16">
          <div className="container-custom">
            <SectionTitle
              title="Our Crown & Bridge Services"
              subtitle="Comprehensive Restorative Solutions for Your Smile"
            />

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
              <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
                <div className="w-12 h-12 bg-dental-offwhite rounded-full flex items-center justify-center mb-4">
                  <Smile className="w-6 h-6 text-dental-highlight" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Porcelain Crowns</h3>
                <p>
                  Highly aesthetic, metal-free crowns that perfectly mimic the appearance of natural teeth while providing excellent durability and strength.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
                <div className="w-12 h-12 bg-dental-offwhite rounded-full flex items-center justify-center mb-4">
                  <Smile className="w-6 h-6 text-dental-highlight" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Porcelain-Fused-to-Metal Crowns</h3>
                <p>
                  A versatile option that combines the strength of a metal base with the natural appearance of porcelain for a balance of durability and aesthetics.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
                <div className="w-12 h-12 bg-dental-offwhite rounded-full flex items-center justify-center mb-4">
                  <Smile className="w-6 h-6 text-dental-highlight" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Zirconia Crowns</h3>
                <p>
                  Ultra-strong, biocompatible crowns that offer exceptional durability while still providing a natural appearance, ideal for back teeth.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
                <div className="w-12 h-12 bg-dental-offwhite rounded-full flex items-center justify-center mb-4">
                  <Smile className="w-6 h-6 text-dental-highlight" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Traditional Bridges</h3>
                <p>
                  Fixed prosthetic devices that replace one or more missing teeth by anchoring to the natural teeth on either side of the gap.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
                <div className="w-12 h-12 bg-dental-offwhite rounded-full flex items-center justify-center mb-4">
                  <Smile className="w-6 h-6 text-dental-highlight" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Cantilever Bridges</h3>
                <p>
                  Specialized bridges used when adjacent teeth are available on only one side of the missing tooth, carefully designed to prevent excess stress.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
                <div className="w-12 h-12 bg-dental-offwhite rounded-full flex items-center justify-center mb-4">
                  <Smile className="w-6 h-6 text-dental-highlight" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Implant-Supported Bridges</h3>
                <p>
                  Premium bridges that are anchored to dental implants rather than natural teeth, offering superior stability and helping to preserve bone health.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-16 bg-dental-offwhite">
          <div className="container-custom">
            <SectionTitle
              title="Why Choose Us for Crowns & Bridges"
              subtitle="Expert Restorative Care at Asrani Dental, Nagpur"
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-12">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold mb-4 flex items-center">
                  <span className="w-8 h-8 rounded-full bg-dental-highlight flex items-center justify-center text-white mr-3">1</span>
                  Precision Craftsmanship
                </h3>
                <p>
                  Our restorations are meticulously designed and created to ensure a perfect fit, optimal function, and natural appearance that seamlessly blends with your smile.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold mb-4 flex items-center">
                  <span className="w-8 h-8 rounded-full bg-dental-highlight flex items-center justify-center text-white mr-3">2</span>
                  Premium Materials
                </h3>
                <p>
                  We use only the highest quality dental materials for our crowns and bridges, ensuring they're durable, biocompatible, and aesthetically pleasing.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold mb-4 flex items-center">
                  <span className="w-8 h-8 rounded-full bg-dental-highlight flex items-center justify-center text-white mr-3">3</span>
                  Advanced Technology
                </h3>
                <p>
                  Our clinic utilizes modern digital scanning and design technologies to create restorations with exceptional accuracy and minimize the number of appointments needed.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold mb-4 flex items-center">
                  <span className="w-8 h-8 rounded-full bg-dental-highlight flex items-center justify-center text-white mr-3">4</span>
                  Comprehensive Care
                </h3>
                <p>
                  We provide detailed aftercare instructions and regular follow-up appointments to ensure your crown or bridge functions properly and lasts for many years.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <ServiceTestimonials
          testimonials={[
            {
              name: "Amit Verma",
              comment: "After breaking my front tooth, I was devastated. Dr. Asrani placed a crown that looks so natural, even my family can't tell which tooth was damaged! The entire process was comfortable and the results are amazing.",
              rating: 5,
              image: "/placeholder.svg"
            },
            {
              name: "Sunita Gupta",
              comment: "I had missing teeth for years and was embarrassed by my smile. The dental bridge I received at Asrani Dental has completely restored my confidence. I can eat normally again and the bridge looks just like natural teeth!",
              rating: 5,
              image: "/placeholder.svg"
            }
          ]}
          title="What Our Crown & Bridge Patients Say"
          subtitle="Real Experiences from Patients at Asrani Dental Clinic in Nagpur"
        />

        {/* FAQ Section */}
        <section className="py-16 bg-dental-offwhite">
          <div className="container-custom">
            <SectionTitle
              title="Frequently Asked Questions"
              subtitle="Common Questions About Crowns & Bridges in Nagpur"
            />

            <div className="mt-12 max-w-3xl mx-auto">
              <FAQAccordion
                faqs={[
                  {
                    question: "How long do dental crowns and bridges last?",
                    answer: "With proper care and maintenance, dental crowns and bridges typically last between 5-15 years or even longer. The lifespan depends on factors like oral hygiene practices, eating habits, material chosen, and regular dental check-ups."
                  },
                  {
                    question: "Is the crown or bridge procedure painful?",
                    answer: "The procedure is performed under local anesthesia, so you shouldn't feel pain during the treatment. Some patients may experience mild sensitivity or discomfort after the procedure, which typically subsides within a few days and can be managed with over-the-counter pain relievers."
                  },
                  {
                    question: "How many appointments are needed for crowns and bridges?",
                    answer: "Traditional crown and bridge procedures usually require two appointments: the first for preparing the teeth and taking impressions, and the second for placing the final restoration. With advanced technology, some clinics offer same-day crowns for certain cases, though bridges typically still require multiple visits."
                  },
                  {
                    question: "How should I care for my crown or bridge?",
                    answer: "Maintain good oral hygiene by brushing twice daily, flossing daily (with special flossers or threaders for bridges), and using an antimicrobial mouthwash. Avoid chewing extremely hard foods or ice with porcelain restorations, and visit your dentist regularly for check-ups and professional cleanings."
                  },
                  {
                    question: "What is the cost of crowns and bridges in Nagpur?",
                    answer: "The cost varies depending on factors like the type of material used, the number of teeth involved, the complexity of the case, and whether additional procedures are needed. At Asrani Dental Clinic, we provide detailed treatment plans with transparent pricing during your consultation and offer various payment options."
                  }
                ]}
              />
            </div>
          </div>
        </section>

        <CTASection
          title="Restore Your Smile with Expert Care"
          description="Regain your confident smile with premium crowns and bridges at Asrani Dental Clinic in Nagpur."
          primaryButtonText="Book Consultation"
          primaryButtonLink="/dental-appointment-booking"
        />
      </main>

      <Footer />
    </>
  );
};

export default CrownsBridges;
